import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  BookOpen,
  CheckCircle2,
  Play,
  Search,
} from "lucide-react";
import { TeachingVideo } from "@/components/TeachingTileCard";
import { teachingMaterials } from "@/lib/teaching-materials";
import { useCity } from "@/contexts/CityContext";
import { useBoardLearn, type BoardTeachingTile } from "@/lib/board";
import { allowedVideoEmbedUrl } from "@/lib/learn";
import { filterSessions, type LearningFilter } from "@/lib/five-minute-cpd";
import "@/styles/five-minute-cpd.css";

export default function BoardLearn() {
  const { city } = useCity();
  const query = useBoardLearn(city.slug);
  return (
    <FiveMinuteLibrary
      tiles={query.data?.tiles ?? []}
      loading={query.isLoading}
      error={query.isError}
      retry={() => void query.refetch()}
    />
  );
}
export function FiveMinuteLibrary({
  tiles,
  loading = false,
  error = false,
  retry,
}: {
  tiles: BoardTeachingTile[];
  loading?: boolean;
  error?: boolean;
  retry: () => void;
}) {
  const [specialty, setSpecialty] = useState("");
  const [search, setSearch] = useState("");
  const [progress, setProgress] = useState<LearningFilter>("all");
  const [videosOnly, setVideosOnly] = useState(false);
  const specialties = useMemo(
    () => Array.from(new Set(tiles.map((tile) => tile.specialtyName))).sort(),
    [tiles],
  );
  const filtered = filterSessions(
    tiles,
    search,
    specialty,
    progress,
    videosOnly,
  );
  const featured = tiles.find(
    (tile) => !tile.completed && allowedVideoEmbedUrl(tile.videoUrl),
  );
  const reset = () => {
    setSpecialty("");
    setSearch("");
    setProgress("all");
    setVideosOnly(false);
  };
  const filtersActive = Boolean(
    search || specialty || progress !== "all" || videosOnly,
  );
  return (
    <div className="pb-five pb-five-channel">
      <div className="pb-channel-banner"><span>PulseBoard presents</span><strong>Five minutes.<br/>A specialist’s perspective.</strong><span>VIDEO · SLIDES · PRACTICAL LEARNING</span></div>
      <header className="pb-five-heading">
        <div>
          <p className="pb-board-eyebrow">
            Specialist insight. Everyday practice.
          </p>
          <h1>
            Five Minute <em>CPD</em>
            <span className="pb-five-dot">.</span>
          </h1>
          <p>
            One practical question. A specialist’s perspective. Something to
            take into your next consultation.
          </p>
        </div>
        <Link className="pb-five-log" to="/board/cpd">
          My learning record <ArrowUpRight size={17} aria-hidden="true" />
        </Link>
      </header>
      {!loading && !error && featured && !filtersActive ? (
        <section className="pb-five-feature" aria-label="Next session">
          <div className="pb-channel-feature-player"><TeachingVideo embedUrl={allowedVideoEmbedUrl(featured.videoUrl)!} title={featured.title} /></div>
          <div className="pb-five-feature-copy">
            <p className="pb-board-eyebrow">
              Next up · {featured.specialtyName}
            </p>
            <h2>
              <Link to={`/board/learn/${featured.id}`}>{featured.title}</Link>
            </h2>
            <p>{featured.specialist.name}</p>
            <small>{featured.specialist.practice}</small>
            <Link to={`/board/learn/${featured.id}`}>
              Make a little room for learning{" "}
              <ArrowUpRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </section>
      ) : null}
      <section className="pb-five-catalogue" aria-labelledby="sessions-heading">
        <div className="pb-five-section-heading">
          <h2 id="sessions-heading">Sessions</h2>
          <span>Built around general practice</span>
        </div>
        <div className="pb-five-filters">
          <label className="pb-five-search">
            <span className="sr-only">Search sessions</span>
            <Search size={18} aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search a topic, specialist or question"
            />
          </label>
          <label>
            <span className="sr-only">Specialty</span>
            <select
              aria-label="Specialty"
              value={specialty}
              onChange={(event) => setSpecialty(event.target.value)}
            >
              <option value="">All specialties</option>
              {specialties.map((item) => (
                <option key={item}>{item}</option>
              ))}
            </select>
          </label>
          <label className="pb-five-checkbox">
            <input
              type="checkbox"
              checked={videosOnly}
              onChange={(event) => setVideosOnly(event.target.checked)}
            />
            Videos only
          </label>
        </div>
        <div className="pb-five-filter-row">
          <div className="pb-five-pills" aria-label="Learning status">
            {(
              [
                ["all", "All sessions"],
                ["unwatched", "To explore"],
                ["completed", "Completed"],
                ["logged", "CPD recorded"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={progress === value}
                onClick={() => setProgress(value)}
              >
                {label}
              </button>
            ))}
          </div>
          {filtersActive ? (
            <button className="pb-five-reset" type="button" onClick={reset}>
              Clear filters
            </button>
          ) : null}
        </div>
        {loading ? (
          <div className="pb-board-state" role="status">
            Loading your learning library…
          </div>
        ) : error ? (
          <div className="pb-board-state pb-board-state--error" role="alert">
            The library could not be loaded.{" "}
            <button type="button" onClick={retry}>
              Try again
            </button>
          </div>
        ) : (
          <>
            <p className="pb-five-count" role="status">
              {filtered.length} {filtered.length === 1 ? "session" : "sessions"}
              {filtersActive ? " matching your filters" : " to explore"}
            </p>
            {filtered.length ? (
              <div className="pb-five-grid">
                {filtered.map((tile) => (
                  <FiveMinuteCard key={tile.id} tile={tile} />
                ))}
              </div>
            ) : (
              <div className="pb-five-empty">
                <BookOpen size={28} aria-hidden="true" />
                <h3>
                  {tiles.length
                    ? "Try a different question"
                    : "Good learning starts with a good question."}
                </h3>
                <p>
                  {tiles.length
                    ? "No sessions match these filters. Try another specialty or clear your search."
                    : "Specialist sessions will appear here after editorial review. Your CPD log is ready for your other learning."}
                </p>
                {tiles.length ? (
                  <button type="button" onClick={reset}>
                    Show all sessions
                  </button>
                ) : (
                  <Link to="/board/cpd">Open my learning record</Link>
                )}
              </div>
            )}
          </>
        )}
      </section>
      <aside className="pb-five-cpd-note">
        <BookOpen size={21} aria-hidden="true" />
        <div>
          <strong>A useful record of the time you spend learning.</strong>
          <p>
            Record your actual learning time as self-directed education.
            PulseBoard does not award CPD points or submit hours to your CPD
            home. Check your home’s requirements; viewing alone does not cover
            every CPD activity type.
          </p>
        </div>
      </aside>
    </div>
  );
}
export function FiveMinuteCard({ tile }: { tile: BoardTeachingTile }) {
  const video = Boolean(allowedVideoEmbedUrl(tile.videoUrl));
  return (
    <article className="pb-five-card">
      <Link
        className="pb-five-card-art"
        data-specialty={tile.specialtySlug}
        to={`/board/learn/${tile.id}`}
        aria-label={`${video ? "Watch" : "Read"} ${tile.title}`}
      >
        <span>{tile.specialtyName}</span>
        <strong aria-hidden="true">
          {video ? "5" : "Aa"}
          <small>{video ? "MINUTE CPD" : "CLINICAL NOTE"}</small>
        </strong>
        <span className="pb-five-card-format">
          {video ? (
            <Play size={15} aria-hidden="true" />
          ) : (
            <BookOpen size={15} aria-hidden="true" />
          )}
          {video ? "Video session" : "Reading session"}
        </span>
      </Link>
      <div className="pb-five-card-copy">
        <h3>
          <Link to={`/board/learn/${tile.id}`}>{tile.title}</Link>
        </h3>
        <p>{tile.specialist.name}</p>
        <small>{tile.specialist.practice}</small>
        {teachingMaterials(tile.body).length ? <p className="pb-material-badge">Slides / materials available</p> : null}
        <footer>
          {tile.cpdLogged ? (
            <span>
              <CheckCircle2 size={14} aria-hidden="true" />
              CPD recorded
            </span>
          ) : tile.completed ? (
            <span>
              <CheckCircle2 size={14} aria-hidden="true" />
              Completed
            </span>
          ) : (
            <span>Ready when you are</span>
          )}
          <ArrowUpRight size={16} aria-hidden="true" />
        </footer>
      </div>
    </article>
  );
}
